import type { SmartBriefs, SmartBriefWithContent } from "@unbox/api";
import { Eye, EyeOff, Lock } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { type FormEvent, useEffect, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { SmartBriefContent, type VideoExample } from "./components/SmartBriefContent";

interface SmartBriefPublicDisplayPageProps {
  shareId: string;
}

export const SmartBriefPublicDisplayPage = ({ shareId }: SmartBriefPublicDisplayPageProps) => {
  const posthog = usePostHog();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [briefData, setBriefData] = useState<{
    brief: SmartBriefs;
    content: SmartBriefWithContent["content"];
    examples?: VideoExample[];
    requiresPassword?: boolean;
  } | null>(null);

  const loadBrief = async (passwordAttempt?: string) => {
    setIsLoading(true);
    setError("");

    try {
      const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
      const url = passwordAttempt ? `${apiUrl}/share/${shareId}/verify` : `${apiUrl}/share/${shareId}`;

      const options: RequestInit = {
        method: passwordAttempt ? "POST" : "GET",
        headers: {
          "Content-Type": "application/json",
        },
      };

      if (passwordAttempt) {
        options.body = JSON.stringify({ password: passwordAttempt });
      }

      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to load brief (${response.status})`);
      }

      const data = await response.json();
      setBriefData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load brief");
    } finally {
      setIsLoading(false);
    }
  };

  // Load brief on mount
  useEffect(() => {
    loadBrief();
  }, [shareId]);

  // Track public brief view in PostHog when content is successfully loaded
  useEffect(() => {
    if (briefData?.brief && briefData?.content && !briefData?.requiresPassword) {
      const eventData = {
        briefId: briefData.brief.id,
        shareId: shareId,
        productName: briefData.brief.productName,
        title: briefData.brief.title,
        isPasswordProtected: briefData.brief.isPasswordProtected || false,
        batchId: briefData.brief.batchId,
        merchantId: briefData.brief.merchantId,
      };

      posthog.capture("public_brief_viewed", eventData);
    }
  }, [briefData, shareId, posthog]);

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await loadBrief(password);
  };

  if (isLoading && !briefData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading brief...</div>
      </div>
    );
  }

  if (error && !briefData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  // Show password form if required
  if (briefData?.requiresPassword && !briefData.content) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-none p-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-blue-600" />
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={password}
                onChange={setPassword}
                isDisabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" color="primary" size="lg" className="w-full" isDisabled={isLoading || !password}>
              {isLoading ? "Verifying..." : "Access Brief"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  if (!briefData?.brief || !briefData.content) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Brief not found</div>
      </div>
    );
  }

  const { brief, content, examples = [] } = briefData;

  return (
    <SmartBriefContent
      brief={brief}
      content={content}
      examples={examples}
      isPublic={true}
      showCompanyLogo={false}
      bannerColor={brief.bannerColor ?? undefined}
    />
  );
};
